/**
 * Act2ChatArea — 第二幕聊天区域
 *
 * 两人对话展示（无亲友），更亲密的约会氛围
 */
import React, { useRef, useEffect } from 'react';
import type { ChatMessage, SenderRole } from '@shared/types/gameState';
import type { Character } from '@shared/types/character';

interface Act2ChatAreaProps {
  messages: ChatMessage[];
  protagonistA: Character;
  protagonistB: Character;
  isTyping: boolean;
  typingSpeaker: string | null;
  streamingText?: string;
  streamingAgentName?: string | null;
  streamingAgentRole?: SenderRole | null;
  currentSpeaker?: string | null;
  /** 卡牌叙事流式状态 */
  isCardNarrating?: boolean;
  cardNarrativeText?: string;
}

export const Act2ChatArea: React.FC<Act2ChatAreaProps> = React.memo(({
  messages,
  protagonistA,
  protagonistB,
  isTyping,
  typingSpeaker,
  streamingText,
  streamingAgentName,
  streamingAgentRole,
  currentSpeaker,
  isCardNarrating,
  cardNarrativeText,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, streamingText, cardNarrativeText]);

  const participants = React.useMemo(() => ({
    [protagonistA.id]: { name: protagonistA.name, avatar: protagonistA.avatar, color: 'bg-coral-500', isA: true },
    [protagonistB.id]: { name: protagonistB.name, avatar: protagonistB.avatar, color: 'bg-purple-500', isA: false },
  }), [protagonistA, protagonistB]);

  const renderMessage = (msg: ChatMessage) => {
    if (msg.type === 'system') {
      return (
        <div key={msg.id} className="flex justify-center my-2">
          <span className="text-[10px] text-[#9CA3AF] bg-[#F5EDE3] px-3 py-1 rounded-full">
            {msg.text}
          </span>
        </div>
      );
    }

    if (msg.type === 'narrative') {
      return (
        <div key={msg.id} className="flex justify-center my-3">
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 px-4 py-2 rounded-xl max-w-[85%] border border-pink-100">
            <p className="text-xs text-[#6B5B4F] italic text-center">{msg.text}</p>
          </div>
        </div>
      );
    }

    if (msg.type === 'thought') {
      return (
        <div key={msg.id} className="flex justify-center my-2">
          <span className="text-[10px] text-[#9B8EC4] italic">
            💭 {msg.text}
          </span>
        </div>
      );
    }

    const p = participants[msg.senderId];
    const isRight = p?.isA ?? false;

    return (
      <div key={msg.id} className={`flex items-end gap-1.5 mb-2 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
        <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-white text-[11px] font-bold ${p?.color || 'bg-gray-400'} overflow-hidden`}>
          {p?.avatar
            ? <img src={p.avatar} className="w-full h-full object-cover" alt={p?.name} />
            : (p?.name?.[0] || '?')
          }
        </div>

        <div className={`max-w-[70%]`}>
          <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
            isRight
              ? 'bg-coral-500 text-white rounded-br-md'
              : 'bg-purple-500 text-white rounded-bl-md'
          }`}>
            {msg.text}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:px-8 no-scrollbar">
      <div className="max-w-2xl mx-auto">
        {messages.map(renderMessage)}

        {/* 卡牌叙事流式气泡 */}
        {isCardNarrating && cardNarrativeText && (
          <div className="flex justify-center my-3">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2 rounded-xl max-w-[85%] border border-amber-200">
              <p className="text-xs text-[#6B5B4F] italic text-center">
                🎬 {cardNarrativeText}
                <span className="inline-block w-0.5 h-3 bg-current ml-0.5 animate-pulse" />
              </p>
            </div>
          </div>
        )}

        {/* 流式消息气泡 */}
        {streamingText && currentSpeaker && (() => {
          const p = participants[currentSpeaker];
          const isRight = p?.isA ?? (streamingAgentRole === 'protagonistA');
          return (
            <div className={`flex items-end gap-1.5 mb-2 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-white text-[11px] font-bold ${p?.color || 'bg-gray-400'} overflow-hidden`}>
                {p?.avatar
                  ? <img src={p.avatar} className="w-full h-full object-cover" alt={p?.name} />
                  : (p?.name?.[0] || streamingAgentName?.[0] || '?')
                }
              </div>
              <div className="max-w-[70%]">
                <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  isRight
                    ? 'bg-coral-500 text-white rounded-br-md'
                    : 'bg-purple-500 text-white rounded-bl-md'
                }`}>
                  {streamingText}
                  <span className="inline-block w-0.5 h-3.5 bg-current ml-0.5 animate-pulse" />
                </div>
              </div>
            </div>
          );
        })()}

        {/* 打字指示器（无流式文本时显示） */}
        {isTyping && typingSpeaker && !streamingText && (
          <div className="flex items-center gap-2 my-2">
            <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-white text-[11px] font-bold ${participants[typingSpeaker]?.color || 'bg-gray-400'}`}>
              {participants[typingSpeaker]?.name?.[0] || '?'}
            </div>
            <div className="bg-[#F5EDE3] px-3 py-2 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>
    </div>
  );
});

Act2ChatArea.displayName = 'Act2ChatArea';
